import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { ReportCardPage } from "@/components/report-card/report-card";
import { combinedToReportCardProps } from "@/lib/from-combined";
import { pb } from "@/lib/pb";
import type { ReportCardCombined } from "@/lib/report-card-helpers";
import { useDocumentTitle } from "@/lib/use-document-title";

/**
 * /marks/$markId — given a single mark row, surface the parent report card
 * for full context (all subjects, totals, rank). MarkCard / MarkGraph were
 * removed; ReportCardPage with subject-tinted accents is now the canonical
 * detail view.
 */
interface MarkRecord {
  id: string;
  student: string;
  exam: string;
  expand?: {
    exam?: { name: string };
  };
}

type FullRcc = ReportCardCombined & {
  exam_name?: string;
  exam_total_marks?: number;
  exam_total_questions?: number;
  created?: string;
};

function markQuery(markId: string, authId: string | undefined) {
  return {
    queryKey: ["mark", markId, authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("mark").getOne<MarkRecord>(markId, {
        expand: "exam",
        fields: "id,student,exam,expand.exam.name",
      }),
  };
}

function rcForMarkQuery(
  studentId: string | undefined,
  examId: string | undefined,
  authId: string | undefined,
) {
  return {
    queryKey: ["mark", "rc", studentId ?? "?", examId ?? "?", authId ?? "anon"] as const,
    queryFn: async (): Promise<FullRcc | null> => {
      if (!studentId || !examId) return null;
      try {
        return await pb
          .collection("report_card_combined")
          .getFirstListItem<FullRcc>(`student_id = '${studentId}' && exam_id = '${examId}'`);
      } catch {
        return null;
      }
    },
  };
}

export const Route = createFileRoute("/_app/marks/$markId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(markQuery(params.markId, pb.authStore.model?.id)),
  component: MarkDetailPage,
});

function MarkDetailPage() {
  const { markId } = Route.useParams();
  const authId = pb.authStore.model?.id;
  const { data: mark } = useSuspenseQuery(markQuery(markId, authId));
  const { data: rc } = useSuspenseQuery(rcForMarkQuery(mark.student, mark.exam, authId));
  useDocumentTitle(mark.expand?.exam?.name ?? "Mark");

  if (!rc) {
    return (
      <div className="text-muted-foreground mx-auto w-full max-w-2xl py-8 text-center text-sm">
        No report card found for this mark yet.
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl">
      <ReportCardPage {...combinedToReportCardProps(rc)} subjectsDetailed />
    </div>
  );
}
